import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  COUNTRY_CONFIG,
  CITIES_BY_COUNTRY,
  STATES_BY_COUNTRY,
  SUBURBS_BY_CITY,
  detectCountry,
  resolveCountryFromHost,
  getCountryConfig,
  formatCurrency,
  validateBusinessNumber,
  validateABN,
  validateNZBN,
} from '../lib/country.js';

const CountryContext = createContext({});

export const useCountry = () => useContext(CountryContext);

/**
 * Production hostnames where the country is the source of truth and the
 * manual toggle should be hidden. Any other host (preview, localhost,
 * staging) keeps the toggle visible so QA can switch between locales.
 */
const PRODUCTION_HOSTS = ['handyhub.co.nz', 'handyhubau.com.au'];

const isProductionHost = (hostname) => {
  if (!hostname) return false;
  const h = String(hostname).toLowerCase().split(':')[0];
  return PRODUCTION_HOSTS.some(prod => h === prod || h === `www.${prod}`);
};

export const CountryProvider = ({ children }) => {
  // Initial country: env override > hostname > localStorage > NZ
  const [countryCode, setCountryCode] = useState(() => detectCountry());

  // Whether the manual country toggle should be visible.
  // Hidden on production hosts (country is locked by domain), visible everywhere else.
  const showToggle = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return !isProductionHost(window.location?.hostname);
  }, []);

  // Persist user choice ONLY when toggle is visible (preview/dev).
  // On production hostname, the host is the source of truth so we don't store.
  useEffect(() => {
    if (showToggle && typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('handyhub_country', countryCode);
    }
  }, [countryCode, showToggle]);

  // Re-sync country when hostname changes (covers cases where the browser
  // back/forward navigates between dev / preview / production)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const fromHost = resolveCountryFromHost(window.location.hostname);
    if (fromHost && fromHost !== countryCode) {
      setCountryCode(fromHost);
    }
  }, []);

  const country = getCountryConfig(countryCode);
  const cities = CITIES_BY_COUNTRY[countryCode] || CITIES_BY_COUNTRY.NZ;
  const states = STATES_BY_COUNTRY[countryCode] || STATES_BY_COUNTRY.NZ;
  const suburbsForCity = (city) => SUBURBS_BY_CITY[city] || [];

  const toggleCountry = () => {
    setCountryCode(prev => (prev === 'NZ' ? 'AU' : 'NZ'));
  };

  const formatPrice = (amount) => formatCurrency(amount, countryCode);
  const validateBN = (value) => validateBusinessNumber(value, countryCode);

  const value = {
    // Core
    countryCode,
    setCountryCode,
    country,
    // Lists
    cities,
    states,
    suburbsForCity,
    // Helpers
    toggleCountry,
    formatPrice,
    formatCurrency: formatPrice,
    validateBusinessNumber: validateBN,
    validateABN,
    validateNZBN,
    // UI gates
    showToggle,
    isProduction: !showToggle,
    // Legacy compatibility
    allCountries: COUNTRY_CONFIG,
    allCities: CITIES_BY_COUNTRY,
  };

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};
